import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';

export interface AiMessage {
  id: string;
  content: string;
  isUser: boolean;
  timestamp: Date;
  isTyping?: boolean;
  functionCalls?: Array<{
    function: string;
    arguments: any;
    result: any;
  }>;
}

export const useAiAssistant = () => {
  const [messages, setMessages] = useState<AiMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isFunctionCalling, setIsFunctionCalling] = useState(false);
  const { toast } = useToast();

  // Function to mark typing as complete
  const markTypingComplete = (messageId: string) => {
    setMessages(prev => prev.map(msg => 
      msg.id === messageId ? { ...msg, isTyping: false } : msg
    ));
  };

  const sendMessage = async (content: string, brandId: string, userId: string) => {
    const userMessage: AiMessage = {
      id: Date.now().toString(),
      content,
      isUser: true,
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);

    // Add typing indicator
    const typingMessage: AiMessage = {
      id: `typing-${Date.now()}`,
      content: '',
      isUser: false,
      timestamp: new Date(),
      isTyping: true,
    };
    setMessages(prev => [...prev, typingMessage]);

    try {
      // Build conversation history for context
      const conversationHistory = messages
        .filter(msg => !msg.isTyping)
        .map(msg => ({
          role: msg.isUser ? 'user' : 'assistant',
          content: msg.content
        }));

      const { data, error } = await supabase.functions.invoke('ai-assistant', {
        body: {
          message: content,
          brandId,
          userId,
          messages: conversationHistory,
        },
      });

      if (error) {
        throw error;
      }

      // Remove typing indicator
      setMessages(prev => prev.filter(msg => !msg.isTyping));

      // Check if function calls were made
      if (data.functionCalls && data.functionCalls.length > 0) {
        setIsFunctionCalling(true);
        
        // Add a function call indicator message
        const functionCallMessage: AiMessage = {
          id: `function-${Date.now()}`,
          content: `🔍 Fetching your latest data...`,
          isUser: false,
          timestamp: new Date(),
          functionCalls: data.functionCalls,
        };
        
        setMessages(prev => [...prev, functionCallMessage]);
        
        // Brief delay to show function call indication
        setTimeout(() => {
          setIsFunctionCalling(false);
          
          // Replace with actual response
          setMessages(prev => prev.map(msg => 
            msg.id === functionCallMessage.id 
              ? {
                  ...msg,
                  content: data.response,
                  isTyping: true, // Start typing effect for response
                }
              : msg
          ));
        }, 500);
      } else {
        const aiMessage: AiMessage = {
          id: `ai-${Date.now()}`,
          content: data.response,
          isUser: false,
          timestamp: new Date(),
          isTyping: true, // Start with typing effect
        };

        setMessages(prev => [...prev, aiMessage]);
      }

    } catch (error) {
      console.error('Error sending message:', error);
      
      // Remove typing indicator
      setMessages(prev => prev.filter(msg => !msg.isTyping));
      setIsFunctionCalling(false);
      
      const errorMessage: AiMessage = {
        id: `error-${Date.now()}`,
        content: "I'm sorry, I encountered an error. Please try again.",
        isUser: false,
        timestamp: new Date(),
      };

      setMessages(prev => [...prev, errorMessage]);

      toast({
        title: "Error",
        description: "Failed to send message. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const clearMessages = () => {
    setMessages([]);
  };

  return {
    messages,
    sendMessage,
    clearMessages,
    isLoading,
    isFunctionCalling,
    markTypingComplete,
  };
};